import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import useSEO from '../hooks/useSEO';

const StayInTouchPage = () => {
    useSEO({
      title: 'Stay in Touch',
      description: 'Subscribe to updates from Veydra. Get insights on simulation, decision intelligence, and systems thinking.'
    });

    const [formData, setFormData] = useState({
        name: '',
        email: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await addDoc(collection(db, 'form-submissions-incremental'), {
                ...formData,
                source: 'stay-in-touch',
                page: '/stay-in-touch',
                intent: 'newsletter',
                createdAt: new Date(),
                lastUpdated: new Date()
            });
            setSubmitStatus('success');
            setFormData({ name: '', email: '' });
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <TopNav />
            
            {/* Hero */}
            <main className="max-w-3xl mx-auto px-8 py-20 md:py-28">
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
                        Stay in touch
                    </h1>
                    <p className="text-xl text-gray-600 max-w-xl mx-auto leading-relaxed">
                        Get occasional updates on new features, research, and simulation insights. No spam, unsubscribe anytime.
                    </p>
                </div>

                {/* Subscribe Form */}
                <div className="bg-gray-50 rounded-xl p-8 max-w-md mx-auto border border-gray-200">
                    {submitStatus === 'success' ? (
                        <div className="text-center py-8">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-medium text-gray-900 mb-2">You're subscribed!</h3>
                            <p className="text-gray-600">Thanks for signing up. We'll be in touch.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {submitStatus === 'error' && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                    Something went wrong. Please try again.
                                </div>
                            )}
                            
                            {/* Name */}
                            <div className="mb-4">
                                <input 
                                    type="text" 
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Your name"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                />
                            </div>

                            {/* Email */}
                            <div className="mb-6">
                                <input 
                                    type="email" 
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Email address"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                    required
                                />
                            </div>

                            {/* Submit Button */}
                            <button 
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                            </button>
                        </form>
                    )}
                    
                    <p className="text-center text-sm text-gray-500 mt-6">
                        We respect your privacy. Read our{' '}
                        <Link to="/privacy" className="text-gray-700 hover:text-gray-900 underline">
                            privacy policy
                        </Link>.
                    </p>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default StayInTouchPage;
